import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { createAppTheme, type ThemeMode } from "./index";

export type ThemePreference = ThemeMode | "auto";

type ThemeModeContextValue = {
  /** Що вибрав користувач: 'light' | 'dark' | 'auto'. */
  preference: ThemePreference;
  /** Поточний ефективний режим — 'auto' резолвиться до 'light'/'dark'. */
  resolved: ThemeMode;
  setPreference: (next: ThemePreference) => void;
};

const STORAGE_KEY = "brewis-theme-mode";
const ThemeModeContext = createContext<ThemeModeContextValue | null>(null);

function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "auto";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "auto") return stored;
  return "auto";
}

function systemPrefersDark(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

/**
 * Обгортає додаток: зберігає preference у localStorage, слухає системну тему
 * для 'auto' і пропонує MUI `ThemeProvider` з динамічною темою.
 */
export function ThemeModeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() =>
    readStoredPreference(),
  );
  const [systemDark, setSystemDark] = useState<boolean>(() => systemPrefersDark());

  // Слухаємо зміну системної теми — оновлюємо лише якщо користувач у 'auto'.
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore (e.g. private mode)
    }
  }, []);

  const resolved: ThemeMode = preference === "auto" ? (systemDark ? "dark" : "light") : preference;

  const theme = useMemo(() => createAppTheme(resolved), [resolved]);

  const value = useMemo<ThemeModeContextValue>(
    () => ({ preference, resolved, setPreference }),
    [preference, resolved, setPreference],
  );

  // Тримаємо meta tag в синку з темою — для мобільних браузерних хромів.
  useEffect(() => {
    const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const color = theme.palette.background.default;
    if (meta) {
      meta.content = color;
    } else {
      const next = document.createElement("meta");
      next.name = "theme-color";
      next.content = color;
      document.head.appendChild(next);
    }
    document.documentElement.style.colorScheme = resolved;
  }, [theme.palette.background.default, resolved]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode(): ThemeModeContextValue {
  const ctx = useContext(ThemeModeContext);
  if (!ctx) throw new Error("useThemeMode must be used within ThemeModeProvider");
  return ctx;
}
